<template>
  <div ref="videoWrapper" :class="isHero ? 'is-hero' : ''">
    <!-- We need this double div with the toggle so video js can properly change the video if the source changes. This is for example the case when we have 2 videos in the PDP stage element and switch between them. -->
    <div
      v-if="toggle"
      ref="divElement"
      class="size-full object-cover"
      :class="
        isHero
          ? 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          : 'relative top-0 left-0'
      "
      @click="togglePlayPause"
    >
      <video ref="videoElement" class="video-js" />
    </div>
    <div
      v-else
      ref="divElement"
      class="size-full top-0 left-0 object-cover"
      :class="isHero ? 'absolute' : 'relative'"
      @click="togglePlayPause"
    >
      <video ref="videoElement" class="video-js" />
    </div>
    <div v-if="isLanding">
      <button
        class="size-full absolute top-0 left-0 z-30"
        @click="togglePlayPause"
      >
        <span class="sr-only">
          {{ videoButtonText }}
        </span>
      </button>
      <div
        class="bg-black/50 text-lightGreen hover:bg-violet absolute px-4 py-2 rounded-full z-20 right-7 bottom-4 text-lg font-regular flex flex-row items-center"
      >
        <div :class="{ paused: isPaused }" class="video-player__control mr-3" />
        <span v-if="isPaused">
          {{ $texts('hero.playLabel') }}
        </span>
        <span v-else> {{ $texts('hero.pauseLabel') }}</span>
      </div>
    </div>
    <button
      v-else
      class="aspect-video size-full absolute top-0 left-0 z-30 w-full cursor-pointer flex items-center justify-center"
      :class="{ hidden: !isPaused }"
      :aria-label="$texts('video.load')"
      @click="togglePlayPause"
    >
      <img :src="poster" class="absolute top-0 left-0 size-full object-cover" />

      <SpriteSymbol name="play" class="size-[75px] z-10" />
    </button>
  </div>
</template>

<script setup lang="ts">
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import type Player from 'video.js/dist/types/player'
import type { DefineImageStyleConfig } from '#rokka/types'
const { $texts } = useNuxtApp()

interface VideoJsPlayerOptions {
  autoplay?: boolean
  controls?: boolean
  loop?: boolean
  sources?: {
    src: string
    type?: string
  }[]
  muted?: boolean
  isLanding?: boolean
  poster?: string
  imageStyle: DefineImageStyleConfig
  isHero?: boolean
  controlBar?: {
    volumePanel?: boolean
  }
  playsinline?: boolean
}

export type MediaVideoProps = {
  videoClass?: string
} & VideoJsPlayerOptions

const props = withDefaults(defineProps<MediaVideoProps>(), {
  // playsinline is needed that the video does not pop to fullscreen automatically on mobile phones. Tested on iPhone.
  autoplay: false,
  playsinline: true,
  controls: true,
  loop: false,
  sources: undefined,
  muted: false,
  videoAriaHidden: false,
  isLanding: false,
  isHero: false,
  preload: 'metadata',
  poster: '',
  controlBar: () => ({ volumePanel: true }),
  videoClass: '',
})

const videoElement = ref<HTMLVideoElement>()
// We need to observe the div with the intersectionObserver since video.js replaces the video element when initialized. This breaks the observer on iOS Safari.
const divElement = ref<HTMLDivElement>()
const player = ref<Player>()
const toggle = ref(false)

const isPaused = ref(!props.autoplay)

const videoButtonText = computed(() => {
  return isPaused.value
    ? $texts('accessibility.video_is_paused')
    : $texts('accessibility.video_is_running')
})

const togglePlayPause = () => {
  if (!player.value) return

  if (isPaused.value) {
    player.value.play()
    isPaused.value = false
  } else {
    // Only pause if controls are disabled
    player.value.pause()
    isPaused.value = true
  }
}

watch(props, () => {
  // player.value.options(newProps) does not work. We need to set the options manually.
  player.value?.controls(props.controls)

  if (player.value?.poster() !== props.poster) {
    player.value?.loadMedia(
      {
        poster: props.poster,
      },
      () => {},
    )
  }

  player.value?.muted(props.muted)
})

// we need our own watcher here for sources.
// watch(props, (newProps, oldProps) => {
// does not work. newProps.sources and oldProps.sources are the same even when switching between videos in the PDP stage element.
watch(
  () => props.sources,
  (newSources, oldSources) => {
    if (
      import.meta.client &&
      oldSources?.length &&
      newSources?.length &&
      oldSources.some(
        (oldSrc) => !newSources.some((newSrc) => oldSrc.src === newSrc.src),
      )
    ) {
      // Only reset the player, when props.sources changes on the client.
      // Otherwise nuxt crashes in SSR context.
      // We reset the player also in onMounted() anyways.
      resetPlayer()
    }
  },
)

const intersectionObserver = ref<IntersectionObserver>()

const initPlayer = () => {
  if (!videoElement.value) {
    return
  }

  const playerOptions: VideoJsPlayerOptions = {
    ...props,
    // set autoplay always to false. We do not want to play the video if it is not in the viewport. With the intersectionObserver with programmatically press play and pause if autoplay is set to true.
    autoplay: false,
  }
  player.value = videojs(
    videoElement.value,
    playerOptions,
    function (this: Player) {
      // Add the videoClass to the video element
      const classes =
        props.videoClass
          ?.split(' ')
          .filter((className) => className.trim().length > 0) ?? []
      if (classes.length > 0) {
        this.player()
          // See https://github.com/videojs/video.js/issues/2617
          .tech({ IWillNotUseThisInPlugins: true })
          .el()
          .classList.add(...classes)
      }
    },
  )
}

const resetPlayer = () => {
  intersectionObserver.value?.disconnect()
  // do not dispose the player here, although it would probably make sense. However we initiallize it so fast again that video.js still tries to do some clean up on the old player which leads to the following warning in the console log:
  // VIDEOJS: WARN: removeClass was called with an element that doesn't exist
  // player.value?.dispose()
  player.value = undefined

  toggle.value = !toggle.value

  // we need next tick here so that the divElement ref points to the correct element after toggle change.
  nextTick(() => {
    if (!divElement.value) {
      return
    }

    // autoplay only when the video is inside the viewport.
    intersectionObserver.value = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        let justInitialized = false
        if (!player.value && entry.isIntersecting) {
          // only init the player on first intersection. This way we avoid loading the first .ts file for all videos on the page.
          initPlayer()
          justInitialized = true
        }

        if (player.value && props.autoplay) {
          if (entry.isIntersecting) {
            player.value.play()
            if (justInitialized) {
              // when switching fast between two videos in the PDP stage element on desktop. Sometimes the video does not start playing. This is a fix for that. Using nextTick() is not enough. We need the 500ms delay.
              setTimeout(() => {
                player.value?.paused()
                if (player.value?.paused()) {
                  player.value.play()
                }
              }, 500)
            }
          } else if (player.value.muted()) {
            player.value.pause()
          }
        }
      })
    })

    intersectionObserver.value.observe(divElement.value)
  })
}

const play = () => {
  player.value?.play()
}

defineExpose({
  play,
})

onMounted(() => {
  resetPlayer()
})

onUnmounted(() => {
  intersectionObserver.value?.disconnect()
  player.value?.dispose()
})

defineOptions({
  name: 'MediaVideoPlayer',
})
</script>

<style lang="postcss">
.is-hero {
  .video-js {
    width: 100%;
    height: 100%;

    video.vjs-tech {
      max-width: unset;
      width: auto;
      margin-left: 50%;
      transform: translateX(-50%);
      min-width: 100%;
      min-height: 100%;
      height: auto;
    }
  }

  :deep(.vjs-poster img) {
    object-fit: cover;
  }
}

div:not(.is-hero) > div {
  > .video-js {
    width: 100%;
    height: auto;

    video.vjs-tech {
      position: relative;
      height: auto;
    }
  }
}

.video-player__control {
  --base-size: 10px;

  @screen lg {
    --base-size: 15px;
  }

  border: 0;
  background: transparent;
  box-sizing: border-box;
  width: 0;
  height: calc(var(--base-size) * 1);
  border-color: transparent transparent transparent theme('colors.lightGreen');
  transition: 100ms all ease;
  cursor: pointer;
  pointer-events: none;

  /* play state */
  border-style: double;
  border-width: 0px 0 0px calc(var(--base-size) * 0.866);

  &.paused {
    border-style: solid;
    border-width: calc(var(--base-size) * 0.5) 0 calc(var(--base-size) * 0.5)
      calc(var(--base-size) * 0.866);
  }

  &:hover {
    border-color: transparent transparent transparent theme('colors.lightGreen');
  }
}
</style>
